import { ComponentType } from 'react';
import { Home } from '../pages/Home';
import { NotFoundPage } from '../pages/NotFound';
import { PurposePage } from '../pages/Purpose';
import { Dashboard } from '../pages/Dashboard';
import { ReporteeInfoPage } from '../pages/ReporteeInfo';
import { ExportFeedbackPage } from '../pages/ExportFeedback';
import { AnalyticsPage } from '../pages/Analytics';
import { ManageQuestionsPage } from '../pages/ManageQuestions';
import { NewFeedbackRequest } from '../pages/NewFeedbackRequest';
import { ViewFeedbackRequest } from '../pages/ViewFeedbackRequest';
import { ProvideManagerReview } from '../pages/ProvideManagerReview';
import { ManageCorePriorities } from '../pages/ManageCorePriorities';
import { PeerFeedbackPage } from '../pages/PeerFeedback';
import { ProvideFeedbackPage } from '../pages/ProvideFeedbackPage';
import { ViewFeedbackPage } from '../pages/ViewFeedbackPage';
import { ProfilePage } from '../pages/Profile';
import { AdminDigestPage } from '../pages/AdminDigest';
import { PseudoManagers } from '../pages/PseudoManagers';

/**
 * Route configuration interface
 */
export interface RouteConfig {
  path: string
  element: ComponentType<any>
  title: string
  description?: string
  protected?: boolean
  roles?: string[]
  showInNav?: boolean
  icon?: string
}

/**
 * Route path constants for type-safe navigation
 * Use these constants throughout your app instead of hardcoded strings
 */
export const ROUTES = {
  Home: '/',
  Purpose: '/purpose',
  Dashboard: '/dashboard',
  Analytics: '/analytics',
  ManageQuestions: '/manage-questions',
  ManageCorePriorities: '/manage-core-priorities',
  ReporteeInfo: '/team',
  ExportFeedback: '/export-feedback',
  NewFeedbackRequest: '/new-feedback-request',
  ViewFeedbackRequest: '/view-feedback-request/:id',
  ProvideManagerReview: '/provide-manager-review/:id',
  PeerFeedback: '/feedback',
  ProvideFeedback: '/feedback/provide/:id',
  ViewFeedback: '/feedback/view/:id',
  Profile: '/profile',
  Admin: '/admin',
  PseudoManagers: '/pseudo-managers',
  NotFound: '*'
} as const

export type RoutePath = typeof ROUTES[keyof typeof ROUTES]

/**
 * All application routes - SINGLE SOURCE OF TRUTH
 * To add a new route: just add a new entry to this array
 */
export const routes: RouteConfig[] = [
  {
    path: ROUTES.Home,
    element: Home,
    title: 'Home',
    description: 'Landing page',
    showInNav: true,
    icon: 'House'
  },
  {
    path: ROUTES.Purpose,
    element: PurposePage,
    title: 'Purpose',
    description: 'Learn about our purpose',
    showInNav: true,
    icon: 'Target'
  },
  {
    path: ROUTES.Dashboard,
    element: Dashboard,
    title: 'Dashboard',
    description: 'Manage feedback requests',
    protected: true,
    showInNav: false,
    icon: 'SquaresFour'
  },
  {
    path: ROUTES.ReporteeInfo,
    element: ReporteeInfoPage,
    title: 'Team',
    description: 'View direct reports hierarchy',
    protected: true,
    showInNav: false,
    icon: 'Users'
  },
  {
    path: ROUTES.Analytics,
    element: AnalyticsPage,
    title: 'Analytics',
    description: 'Team performance analytics',
    protected: true,
    showInNav: false,
    icon: 'BarChart3'
  },
  {
    path: ROUTES.ExportFeedback,
    element: ExportFeedbackPage,
    title: 'Export Connect',
    description: 'Export employee connect as PDF',
    protected: true,
    showInNav: false,
    icon: 'FileDown'
  },
  {
    path: ROUTES.ManageQuestions,
    element: ManageQuestionsPage,
    title: 'Manage Questions',
    description: 'Configure custom feedback questions',
    protected: true,
    showInNav: false,
    icon: 'Settings'
  },
  {
    path: ROUTES.ManageCorePriorities,
    element: ManageCorePriorities,
    title: 'Core Priorities',
    description: 'Set core priorities for direct reports',
    protected: true,
    showInNav: false,
    icon: 'Target'
  },
  {
    path: ROUTES.NewFeedbackRequest,
    element: NewFeedbackRequest,
    title: 'New Connect Request',
    description: 'Create a new connect request',
    protected: true,
    showInNav: false,
    icon: 'Plus'
  },
  {
    path: ROUTES.ViewFeedbackRequest,
    element: ViewFeedbackRequest,
    title: 'View Connect Request',
    description: 'View connect request details',
    protected: true,
    showInNav: false,
    icon: 'Eye'
  },
  {
    path: ROUTES.ProvideManagerReview,
    element: ProvideManagerReview,
    title: 'Provide Manager Review',
    description: 'Review employee feedback and provide manager comments',
    protected: true,
    showInNav: false,
    icon: 'UserCheck'
  },
  {
    path: ROUTES.PeerFeedback,
    element: PeerFeedbackPage,
    title: 'Feedback',
    description: 'Peer feedback requests and responses',
    protected: true,
    showInNav: false,
    icon: 'MessageSquareMore'
  },
  {
    path: ROUTES.ProvideFeedback,
    element: ProvideFeedbackPage,
    title: 'Provide 360 Feedback',
    description: 'Submit responses to a peer feedback request',
    protected: true,
    showInNav: false,
    icon: 'MessageSquare'
  },
  {
    path: ROUTES.ViewFeedback,
    element: ViewFeedbackPage,
    title: 'View Feedback',
    description: 'View received peer feedback responses',
    protected: true,
    showInNav: false,
    icon: 'Eye'
  },
  {
    path: ROUTES.Profile,
    element: ProfilePage,
    title: 'Profile',
    description: 'My profile and CV',
    protected: true,
    showInNav: false,
    icon: 'User'
  },
  {
    path: ROUTES.Admin,
    element: AdminDigestPage,
    title: 'Admin',
    description: 'Digest preview and send (admin-only)',
    protected: true,
    showInNav: false,
    icon: 'Shield'
  },
  {
    path: ROUTES.PseudoManagers,
    element: PseudoManagers,
    title: 'Pseudo Managers',
    description: 'Assign and review Pseudo Managers for your reportees',
    protected: true,
    showInNav: true,
    icon: 'UserCog'
  },
  {
    path: ROUTES.NotFound,
    element: NotFoundPage,
    title: 'Not Found',
    description: '404 Page',
    showInNav: false
  }
]

/**
 * Get routes filtered by criteria
 */
export const getRoutes = (options?: {
  showInNav?: boolean
  protected?: boolean
  role?: string
}): RouteConfig[] => {
  let filteredRoutes = [...routes]

  if (options?.showInNav !== undefined) {
    filteredRoutes = filteredRoutes.filter(r => r.showInNav === options.showInNav)
  }

  if (options?.protected !== undefined) {
    filteredRoutes = filteredRoutes.filter(r => r.protected === options.protected)
  }

  if (options?.role) {
    filteredRoutes = filteredRoutes.filter(r => 
      !r.roles || r.roles.length === 0 || (options.role && r.roles.includes(options.role))
    )
  }

  return filteredRoutes
}

/**
 * Get route by path
 */
export const getRouteByPath = (path: string): RouteConfig | undefined => {
  return routes.find(r => r.path === path)
}
